import { OperationVariables } from '@apollo/react-common';
import { useQuery } from 'hooks/useQuery';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect } from 'react';
import Config from 'react-native-config';
import { useDispatch, useSelector } from 'react-redux';
import { GetMedicalTestResultListByEncounter } from 'services/orchestration/GetMedicalTestResultListByEncounter';
import { getTestResultListByEncounter } from 'store/testResults/actions';
import {
  testResultListByEncounterDataSelector,
  testResultListByEncounterIsFetchingSelector,
  testResultListByEncounterErrorSelector
} from 'store/testResults/selectors';

interface GetMedicalTestResultListByEncounterProps {
  testResultDate: Date | Dayjs | string;
  encounterId: string;
  customGqlOptions?: OperationVariables;
}

const queryOptions = {
  notifyOnNetworkStatusChange: true,
  context: {
    clientName: 'orchestration',
    useFhir: true
  }
};

export const useGetMedicalTestResultListByEncounter = (
  props: GetMedicalTestResultListByEncounterProps
) => {
  const { testResultDate, encounterId, customGqlOptions = {} } = props || {};
  const restData = useSelector(testResultListByEncounterDataSelector);
  const restIsFetching = useSelector(testResultListByEncounterIsFetchingSelector);
  const restError = useSelector(testResultListByEncounterErrorSelector);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!!Config.TEST_RESULTS_EXPANSION_ENABLED) {
      dispatch(getTestResultListByEncounter({ encounterId }));
    }
  }, []);

  const useQueryResult = useQuery(GetMedicalTestResultListByEncounter, {
    variables: {
      testResultDate: dayjs(testResultDate).format('YYYY-MM-DD'),
      source: 'MILLENNIUM', // all test results will come from Millennium source
      encounterId
    },
    ...queryOptions,
    ...customGqlOptions,
    skip: customGqlOptions?.skip || !!Config.TEST_RESULTS_EXPANSION_ENABLED
  });

  if (!!Config.TEST_RESULTS_EXPANSION_ENABLED) {
    return {
      data: restData,
      dataErrors: [],
      loading: restIsFetching,
      error: restError,
      refetch: () => dispatch(getTestResultListByEncounter({ encounterId }))
    };
  }

  return {
    ...useQueryResult,
    data: useQueryResult?.data?.getMedicalTestResultListByEncounter?.results || [],
    dataErrors: useQueryResult?.data?.getMedicalTestResultListByEncounter?.errors || []
  };
};
